// PWA Service Worker Registration using VitePWA's built-in system
import { registerSW } from 'virtual:pwa-register';

export interface PWAUpdateHandler {
  onNeedRefresh?: () => void;
  onOfflineReady?: () => void;
}

// Store the update function so we can trigger updates from anywhere
let updateSWFunction: ((reloadPage?: boolean) => Promise<void>) | undefined;

// Track whether an update is pending so we can auto-apply on next navigation
let updatePending = false;

export const registerPWA = (handlers: PWAUpdateHandler = {}) => {
  updateSWFunction = registerSW({
    immediate: true,
    onNeedRefresh() {
      console.log('[PWA] New content available, update ready');
      updatePending = true;
      handlers.onNeedRefresh?.();
    },
    onOfflineReady() {
      console.log('[PWA] App is ready to work offline');
      handlers.onOfflineReady?.();
    },
    onRegisteredSW(swUrl, registration) {
      console.log('[PWA] Service Worker registered:', swUrl);

      // Check for updates every 10 minutes
      if (registration) {
        setInterval(() => {
          registration.update();
        }, 10 * 60 * 1000);
      }
    },
    onRegisterError(error) {
      console.error('[PWA] Service Worker registration failed:', error);
    },
  });

  // Auto-apply pending updates when user navigates back to the tab
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && updatePending) {
      console.log('[PWA] Tab focused with pending update, applying...');
      applyUpdate();
    }
  });

  return updateSWFunction;
};

// Apply a pending update: activate new SW and reload
const applyUpdate = async () => {
  updatePending = false;
  if (updateSWFunction) {
    console.log('[PWA] Activating new service worker...');
    await updateSWFunction(true); // true = reload the page after update
  } else {
    // Fallback: skip SW cache entirely
    window.location.reload();
  }
};

// Call this to activate the new service worker and reload
export const updateServiceWorker = applyUpdate;

// Force check for updates
export const checkForUpdates = async () => {
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      await registration.update();
      console.log('[PWA] Checked for updates');
    }
  }
};

// Dynamic manifest generation based on courier info
export const updateManifest = (courierName: string, courierThemeColor?: string) => {
  const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;

  if (manifestLink) {
    const manifest = {
      name: `${courierName} - Admin`,
      short_name: courierName,
      description: `${courierName} Admin Dashboard`,
      start_url: '/',
      display: 'standalone',
      background_color: '#ffffff',
      theme_color: courierThemeColor || '#6366f1',
      orientation: 'portrait-primary',
      scope: '/',
      icons: [
        { src: '/icons/icon-72x72.png', sizes: '72x72', type: 'image/png', purpose: 'any maskable' },
        { src: '/icons/icon-96x96.png', sizes: '96x96', type: 'image/png', purpose: 'any maskable' },
        { src: '/icons/icon-128x128.png', sizes: '128x128', type: 'image/png', purpose: 'any maskable' },
        { src: '/icons/icon-144x144.png', sizes: '144x144', type: 'image/png', purpose: 'any maskable' },
        { src: '/icons/icon-152x152.png', sizes: '152x152', type: 'image/png', purpose: 'any maskable' },
        { src: '/icons/icon-192x192.png', sizes: '192x192', type: 'image/png', purpose: 'any maskable' },
        { src: '/icons/icon-384x384.png', sizes: '384x384', type: 'image/png', purpose: 'any maskable' },
        { src: '/icons/icon-512x512.png', sizes: '512x512', type: 'image/png', purpose: 'any maskable' }
      ],
      categories: ['business', 'productivity'],
      shortcuts: [
        {
          name: 'Dashboard',
          short_name: 'Dashboard',
          description: 'Go to Dashboard',
          url: '/dashboard',
          icons: [{ src: '/icons/icon-96x96.png', sizes: '96x96', type: 'image/png' }]
        },
        {
          name: 'Packages',
          short_name: 'Packages',
          description: 'View Packages',
          url: '/packages',
          icons: [{ src: '/icons/icon-96x96.png', sizes: '96x96', type: 'image/png' }]
        },
        {
          name: 'Customers',
          short_name: 'Customers',
          description: 'View Customers',
          url: '/customers',
          icons: [{ src: '/icons/icon-96x96.png', sizes: '96x96', type: 'image/png' }]
        }
      ]
    };

    const manifestString = JSON.stringify(manifest);
    const blob = new Blob([manifestString], { type: 'application/json' });
    const manifestURL = URL.createObjectURL(blob);
    manifestLink.href = manifestURL;
  }

  // Update theme color meta tag
  const themeColorMeta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;
  if (themeColorMeta && courierThemeColor) {
    themeColorMeta.content = courierThemeColor;
  }

  // Update app title
  const titleTag = document.querySelector('title');
  if (titleTag) {
    titleTag.textContent = `${courierName} - Admin`;
  }
};

// Install prompt handling
let deferredPrompt: BeforeInstallPromptEvent | null = null;

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const setupInstallPrompt = (onInstallPromptAvailable?: (prompt: BeforeInstallPromptEvent) => void) => {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    console.log('[PWA] Install prompt available');
    onInstallPromptAvailable?.(deferredPrompt);
  });

  window.addEventListener('appinstalled', () => {
    console.log('[PWA] App was installed');
    deferredPrompt = null;
  });
};

export const showInstallPrompt = async () => {
  if (!deferredPrompt) {
    console.warn('[PWA] Install prompt not available');
    return false;
  }

  deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  console.log('[PWA] User choice:', outcome);
  deferredPrompt = null;
  return outcome === 'accepted';
};

export const isInstalled = () => {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
};

// Force refresh: clears all caches, unregisters service worker, and reloads
export const forceRefresh = async (redirectTo?: string): Promise<void> => {
  console.log('[PWA] Force refresh initiated...');

  try {
    // 1. Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      console.log(`[PWA] Unregistering ${registrations.length} service worker(s)...`);
      await Promise.all(registrations.map((reg) => reg.unregister()));
    }

    // 2. Clear all caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      console.log(`[PWA] Clearing ${cacheNames.length} cache(s)...`);
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }

    // 3. Clear all local & session storage (full clean slate)
    localStorage.clear();
    sessionStorage.clear();

    // 4. Clear accessible cookies
    document.cookie.split(';').forEach((c) => {
      const name = c.split('=')[0].trim();
      if (name) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      }
    });

    console.log('[PWA] Force refresh complete, reloading page...');

    // 5. Redirect to specified path or reload
    if (redirectTo) {
      window.location.href = window.location.origin + redirectTo;
    } else {
      window.location.href = window.location.origin + '/';
    }
  } catch (error) {
    console.error('[PWA] Force refresh failed:', error);
    window.location.href = window.location.origin + '/';
  }
};

// Check if refresh is requested via query param
export const checkForRefreshParam = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('refresh') === 'true';
};
